<template>
    <l-card-content>
        <l-headline
            :title="$t('surveys.details.titleVisibility')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            :tabs="tabs"
            btn-test-id="surveys-edit-visibility"
        >
        </l-headline>

        <hr class="visible-xs" />
        <span>{{ $t("surveys.details.labelVisibilityOwner") }} </span>

        <hr />

        <div class="row">
            <div class="col-md-3" style="margin-top: 10px">
                <div class="color-dark tw-text-right tw-font-bold" style="font-size: 16px">
                    {{ $t("surveys.details.titleVisibilityParticipants") }}
                </div>
            </div>
            <div class="col-md-9">
                <l-select
                    v-model="sharedWithDefault.level"
                    class=""
                    :options="visibilityOptions"
                    @change="updateVisibility()"
                />
                <hr />
            </div>
            <!-- available views-->
            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableViews") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span
                        v-for="key in getActiveViews(sharedWithDefault, 'global')"
                        :key="key"
                        class="togglebutton"
                    >
                        <label>
                            <input
                                type="checkbox"
                                :checked="sharedWithDefault.scope.includes(key)"
                                @click="changeSharedWithScope(sharedWithDefault, key)"
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{ $t(`surveys.details.scope.${key}`) }}</span>
                        </label>
                    </span>
                </div>
                <hr />
            </div>
            <!-- available benchmark-->
            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableBenchmark") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span class="togglebutton">
                        <label>
                            <input
                                type="checkbox"
                                :disabled="!sharedWithDefault.scope.includes('HEAT_MAP')"
                                :checked="sharedWithDefault.scope.includes('BENCHMARK_INTERNAL')"
                                @click="
                                    changeSharedWithScope(sharedWithDefault, 'BENCHMARK_INTERNAL')
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.scope.BENCHMARK_INTERNAL`)
                            }}</span>
                        </label>
                    </span>

                    <span class="togglebutton xl:tw-col-span-2">
                        <label>
                            <input
                                type="checkbox"
                                :disabled="!sharedWithDefault.scope.includes('LIST')"
                                :checked="sharedWithDefault.scope.includes('BENCHMARK_EXTERNAL')"
                                @click="
                                    changeSharedWithScope(sharedWithDefault, 'BENCHMARK_EXTERNAL')
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.scope.BENCHMARK_EXTERNAL`)
                            }}</span>
                        </label>
                    </span>
                </div>
                <hr />
            </div>

            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableFilters") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span v-for="key in demographicFilters" :key="key" class="togglebutton">
                        <label>
                            <input
                                type="checkbox"
                                :checked="sharedWithDefault.filters.includes(key)"
                                @click="changeSharedWithFilters(sharedWithDefault, key)"
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.filters_${key}`)
                            }}</span>
                        </label>
                    </span>
                </div>
            </div>
        </div>

        <hr />

        <h4 class="color-dark">
            <strong
                ><span>{{ $t("surveys.details.titleVisibilitySpecific") }}</span></strong
            >
        </h4>

        <hr />

        <div v-for="(accessHolder, index) in survey.settings.sharedWith" :key="index" class="row">
            <div class="col-md-3" style="margin-top: 15px;font-size: 15px">
                <div class="tw-text-right color-dark">
                    <span
                        class="clickable"
                        tabindex="0"
                        @click="
                            () => {
                                survey.settings.sharedWith.splice(index, 1);
                                updateVisibility();
                            }
                        "
                        @keyup.enter="
                            () => {
                                survey.settings.sharedWith.splice(index, 1);
                                updateVisibility();
                            }
                        "
                    >
                        <i class="fal fa-trash color-primary r-default-margin-s"></i>
                    </span>

                    <span v-if="accessHolder.user" class="tw-font-bold">
                        {{ accessHolder.user.firstname }} {{ accessHolder.user.lastname }}
                    </span>
                    <span v-if="accessHolder.localTeam" class="tw-font-bold">
                        {{ accessHolder.localTeam.name }} (Team)
                    </span>
                </div>
            </div>
            <div class="col-md-9" style="margin-top: 5px">
                <l-select
                    v-model="survey.settings.sharedWith[index].level"
                    :options="allLevelOptions"
                    @change="updateVisibility()"
                />

                <hr />
            </div>
            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableViews") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span
                        v-for="(key, idx) in getActiveViews(
                            survey.settings.sharedWith[index],
                            'custom',
                            accessHolder,
                        )"
                        :key="idx"
                        class="togglebutton"
                    >
                        <label
                            v-b-popover.hover.top="
                                key === 'CONVERSATIONS'
                                    ? $t('surveys.details.conversationsScopeHint')
                                    : ''
                            "
                        >
                            <input
                                type="checkbox"
                                :checked="survey.settings.sharedWith[index].scope.includes(key)"
                                @click="
                                    changeSharedWithScope(
                                        survey.settings.sharedWith[index],
                                        key,
                                        accessHolder,
                                    )
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{ $t(`surveys.details.scope.${key}`) }}</span>
                        </label>
                    </span>
                </div>
                <hr />
            </div>

            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableBenchmark") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span class="togglebutton">
                        <label>
                            <input
                                type="checkbox"
                                :disabled="
                                    !survey.settings.sharedWith[index].scope.includes('HEAT_MAP')
                                "
                                :checked="
                                    survey.settings.sharedWith[index].scope.includes(
                                        'BENCHMARK_INTERNAL',
                                    )
                                "
                                @click="
                                    changeSharedWithScope(
                                        survey.settings.sharedWith[index],
                                        'BENCHMARK_INTERNAL',
                                    )
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.scope.BENCHMARK_INTERNAL`)
                            }}</span>
                        </label>
                    </span>

                    <span class="togglebutton xl:tw-col-span-2">
                        <label>
                            <input
                                type="checkbox"
                                :disabled="
                                    !survey.settings.sharedWith[index].scope.includes('LIST')
                                "
                                :checked="
                                    survey.settings.sharedWith[index].scope.includes(
                                        'BENCHMARK_EXTERNAL',
                                    )
                                "
                                @click="
                                    changeSharedWithScope(
                                        survey.settings.sharedWith[index],
                                        'BENCHMARK_EXTERNAL',
                                    )
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.scope.BENCHMARK_EXTERNAL`)
                            }}</span>
                        </label>
                    </span>
                </div>
                <hr />
            </div>

            <div class="col-md-3">
                <div class="color-dark tw-text-right" style="font-size: 16px">
                    {{ $t("surveys.details.labelAvailableFilters") }}
                </div>
            </div>
            <div class="col-md-9">
                <div class="tw-grid tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
                    <span v-for="(key, idx) in demographicFilters" :key="idx" class="togglebutton">
                        <label>
                            <input
                                type="checkbox"
                                :checked="survey.settings.sharedWith[index].filters.includes(key)"
                                @click="
                                    changeSharedWithFilters(survey.settings.sharedWith[index], key)
                                "
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`surveys.details.filters_${key}`)
                            }}</span>
                        </label>
                    </span>
                </div>
                <hr />
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <l-input-typeahead
                    style="margin-top: 30px;"
                    :label="$t('surveys.details.labelAddParticipants')"
                    :placeholder="$t(`surveys.details.placeholderEmployee`)"
                    :data="holderPool"
                    :on-select="sharingAdd"
                    :on-input-change="typeaheadHolderOnInputChange"
                    data-test-id="surveys-detail-participants"
                />
            </div>
        </div>

        <hr />

        <div
            v-show="error && error.message"
            id="error"
            class="alert alert-info"
            data-test-id="survey-settings-visibility-error"
        >
            <span>{{ error.message }}</span>
        </div>

        <div
            v-show="visibilitySuccess"
            id="error"
            class="alert alert-success"
            data-test-id="survey-settings-visibility-success"
        >
            <span>{{ $t("surveys.details.labelVisibilityUpdated") }}</span>
        </div>

        <div class="row">
            <div class="col-md-3 col-md-offset-9">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    type="button"
                    data-test-id="survey-settings-visibility-update"
                    @click="$router.push(`/surveys/edit/${$route.params.id}/questions`)"
                >
                    <span>{{ $t("surveys.details.btnSaveChanges") }} </span>
                </button>
            </div>
        </div>
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";

import icon from "@/img/icons/surveys.png";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputTypeahead,
        LSelect,
    },
    props: {
        openHelpHC: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            autoSavingText: "",

            demographicFilters: [
                "manager",
                "localTeam",
                "location",
                "customRole",
                "gender",
                "age",
                "performance",
            ],
            allViews: [
                "LIST",
                "COMMENTS",
                "CONVERSATIONS",
                "HEAT_MAP",
                "PARTICIPATION_RATE",
                "MULTI",
                "ACTION_ITEMS",
            ],
            allBenchmarks: ["BENCHMARK_EXTERNAL", "BENCHMARK_INTERNAL"],

            survey: {},
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            getUserList: "users/list/list",

            visibilitySuccess: "surveys/surveys/visibilitySuccess",
            getDetail: "surveys/surveys/detail",

            error: "surveys/surveys/error",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
        allLevelOptions() {
            return [
                {
                    value: "",
                    text: this.$t("surveys.details.optionSharedNothing"),
                },
                {
                    value: "TEAM_MANAGER",
                    text: this.$t("surveys.details.optionManagedUserTeams"),
                },
                {
                    value: "DIRECT_REPORTS",
                    text: this.$t("surveys.details.optionSharedDirectReports"),
                },
                {
                    value: "TEAM_MEMBER",
                    text: this.$t("surveys.details.optionSharedUserTeams"),
                },
                {
                    value: "ALL",
                    text: this.$t("surveys.details.optionSharedAllResults"),
                },
            ];
        },
        sharedWithDefault() {
            return (
                this.survey.settings?.sharedWithDefault || {
                    scope: [],
                    level: "",
                    filters: [],
                }
            );
        },

        holderPool() {
            return [
                ...this.usersSearchList
                    .filter(user => user._id !== this.user._id) // you don't need to share with yourself.
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: user.firstname + " " + user.lastname,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                })),
            ];
        },
        visibilityOptions() {
            return [
                {
                    value: "",
                    text: this.$t("surveys.details.optionSharedNothing"),
                },
                {
                    value: "TEAM_MANAGER",
                    text: this.$t("surveys.details.optionManagedUserTeams"),
                },
                {
                    value: "DIRECT_REPORTS",
                    text: this.$t("surveys.details.optionSharedDirectReports"),
                },
                {
                    value: "TEAM_MEMBER",
                    text: this.$t("surveys.details.optionSharedUserTeams"),
                },
                {
                    value: "ALL",
                    text: this.$t("surveys.details.optionSharedAllResults"),
                },
            ];
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
            },
        },
    },

    created() {
        this.icon = icon;
    },
    methods: {
        changeSharedWithScope(sharedWith, key, accessHolder = {}) {
            const scope = sharedWith.scope ?? [];
            const index = scope.indexOf(key);
            if (index !== -1) {
                scope.splice(index, 1);

                if (key === "HEAT_MAP" && scope.includes("BENCHMARK_INTERNAL")) {
                    // remove internal benchmark if user doesn't have HEAT_MAP permission
                    scope.splice(scope.indexOf("BENCHMARK_INTERNAL"), 1);
                }
                if (key === "LIST" && scope.includes("BENCHMARK_EXTERNAL")) {
                    // remove external benchmark if user doesn't have HEAT_MAP permission
                    scope.splice(scope.indexOf("BENCHMARK_EXTERNAL"), 1);
                }

                if (key === "COMMENTS" && accessHolder.user) {
                    // Users: remove conversations permissions if COMMENTS access is not granted
                    scope.splice(scope.indexOf("CONVERSATIONS"), 1);
                }
            } else {
                scope.push(key);

                if (key === "CONVERSATIONS" && accessHolder.user && !scope.includes("COMMENTS")) {
                    // Users: enable COMMENTS as well if not done yet
                    scope.push("COMMENTS");
                }
            }

            if (scope.length === 0) {
                sharedWith.level = "";
            }
            sharedWith.scope = sharedWith.scope.filter(Boolean);

            this.updateVisibility();
        },
        getActiveViews(sharedWith, type = "global", accessHolder = {}) {
            let views = this.allViews;

            if (sharedWith?.level !== "ALL") {
                // participation rate only contains data for all results and should not
                // be included in other selections
                views = views.filter(view => view !== "PARTICIPATION_RATE");
            }

            if (type !== "custom" || !accessHolder.user) {
                // we allow conversation participation rules for custom permission
                // definitions only
                views = views.filter(view => view !== "CONVERSATIONS");
            }

            return views;
        },
        changeSharedWithFilters(sharedWith, key) {
            var filters = sharedWith.filters;
            var index = filters.indexOf(key);
            if (index !== -1) {
                filters.splice(index, 1);
            } else {
                filters.push(key);
            }
            this.updateVisibility();
        },
        sharingAdd(item) {
            // check whether already present

            var alreadyPresent = false;

            const { sharedWith } = this.survey.settings;

            sharedWith.forEach(accessHolder => {
                if (
                    (accessHolder.user && accessHolder.user._id == item._id) ||
                    (accessHolder.localTeam && accessHolder.localTeam._id == item._id)
                ) {
                    alreadyPresent = true;
                }
            });

            if (alreadyPresent) {
                return;
            }

            // add

            sharedWith.push({
                user: item.type == "user" ? item : null,
                localTeam: item.type == "team" ? item : null,
                scope: [...this.allViews, ...this.allBenchmarks],
                filters: ["manager", "localTeam", "location", "customRole", "gender", "age"],
                level: "ALL",
            });

            this.updateVisibility();
        },
        updateVisibility() {
            // Warning: This component contains timing issues which is why we need to use nextTick to make sure
            // values are updated
            this.$nextTick(() => {
                let { sharedWithDefault, sharedWith } = this.survey.settings;

                // some settings are not enabled for all levels and need to get filtered out for every update
                if (sharedWithDefault.level !== "ALL") {
                    sharedWithDefault.scope = sharedWithDefault.scope?.filter(
                        view => view !== "PARTICIPATION_RATE",
                    );
                }

                sharedWithDefault.scope = sharedWithDefault.scope?.filter(
                    view => view !== "CONVERSATIONS",
                );

                for (const sharedWithUser of sharedWith) {
                    if (sharedWithUser.level !== "ALL") {
                        sharedWithUser.scope = sharedWithUser.scope?.filter(
                            view => view !== "PARTICIPATION_RATE",
                        );
                    }
                }

                this.autoSavingText = this.$t(`surveys.details.autoSaving`);
                this.$store
                    .dispatch("surveys/surveys/updateVisibility", {
                        payload: {
                            sharedWith,
                            sharedWithDefault,
                            survey: this.survey._id,
                        },
                    })
                    .then(() => {
                        this.autoSavingText = "";
                    });
            });
        },

        typeaheadHolderOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
    },
};
</script>

<style scoped></style>
